/**
 * Spread-scope layouts: slot fractions are of the WHOLE spread (2 pages wide by 1 page tall).
 * Ids are stable — the document stores `spread.layoutId` and elements keep `slotId`.
 */
import type { Layout } from '@freealbum/document';
import {
  ALL_FAMILIES,
  FULL_BOX,
  GUTTER_FRAC,
  TALL_FAMILIES,
  WIDE_FAMILIES,
  band,
  grid,
  halves,
  innerBox,
  layout,
  slot,
  sub,
} from './_helpers';

const box = innerBox('spread');
const [L, R] = halves(box);
const G = GUTTER_FRAC;
const GX = GUTTER_FRAC * 0.5;

export const SPREAD_LAYOUTS: Layout[] = [
  // ---------- 1 photo ----------
  layout({
    id: 'sp-1-panorama',
    name: 'Panorama across the fold',
    scope: 'spread',
    families: ALL_FAMILIES,
    tags: ['hero', 'full-bleed', 'panorama', 'crosses-fold'],
    marginFrac: 0,
    slots: [slot({ id: 'a', ...FULL_BOX })],
  }),
  layout({
    id: 'sp-1-center',
    name: 'One photo, wide margins',
    scope: 'spread',
    families: ALL_FAMILIES,
    tags: ['hero', 'calm', 'single'],
    slots: [slot({ id: 'a', ...sub(box, 0.12, 0.08, 0.76, 0.84) })],
  }),
  layout({
    id: 'sp-1-hero-title',
    name: 'Photo left, title right',
    scope: 'spread',
    families: ALL_FAMILIES,
    tags: ['hero', 'title', 'chapter'],
    slots: [
      slot({ id: 'a', ...L }),
      slot({ id: 't', kind: 'text', role: 'title', ...sub(R, 0.08, 0.32, 0.84, 0.2) }),
      slot({ id: 'b', kind: 'text', role: 'body', ...sub(R, 0.08, 0.55, 0.84, 0.22) }),
    ],
  }),
  layout({
    id: 'sp-1-quote',
    name: 'Quote left, photo right',
    scope: 'spread',
    families: ALL_FAMILIES,
    tags: ['title', 'quote', 'chapter'],
    slots: [
      slot({ id: 't', kind: 'text', role: 'title', ...sub(L, 0.1, 0.34, 0.8, 0.18) }),
      slot({ id: 'b', kind: 'text', role: 'body', ...sub(L, 0.1, 0.55, 0.8, 0.2) }),
      slot({ id: 'a', ...R }),
    ],
  }),
  layout({
    id: 'sp-1-full-bleed-left',
    name: 'Full-bleed left page',
    scope: 'spread',
    families: ALL_FAMILIES,
    tags: ['full-bleed', 'hero'],
    marginFrac: 0,
    slots: [slot({ id: 'a', x: 0, y: 0, w: 0.5, h: 1 })],
  }),

  // ---------- 2 photos ----------
  layout({
    id: 'sp-2-side',
    name: 'Two side by side',
    scope: 'spread',
    families: ALL_FAMILIES,
    tags: ['pair', 'symmetry'],
    slots: [slot({ id: 'a', ...L }), slot({ id: 'b', ...R })],
  }),
  layout({
    id: 'sp-2-full-bleed',
    name: 'Two full-bleed pages',
    scope: 'spread',
    families: ALL_FAMILIES,
    tags: ['pair', 'full-bleed', 'bold'],
    marginFrac: 0,
    slots: [slot({ id: 'a', x: 0, y: 0, w: 0.5, h: 1 }), slot({ id: 'b', x: 0.5, y: 0, w: 0.5, h: 1 })],
  }),
  layout({
    id: 'sp-2-stacked',
    name: 'Two wide, stacked',
    scope: 'spread',
    families: WIDE_FAMILIES,
    tags: ['stack', 'wide', 'crosses-fold'],
    slots: grid(box, 'spread', { cols: 1, rows: 2 }).map((s, i) => ({ ...s, id: i === 0 ? 'a' : 'b' })),
  }),
  layout({
    id: 'sp-2-hero-left',
    name: 'Big left, small right',
    scope: 'spread',
    families: ALL_FAMILIES,
    tags: ['hero', 'asymmetric'],
    slots: [
      slot({ id: 'a', ...sub(box, 0, 0, 0.6 - GX, 1) }),
      slot({ id: 'b', ...sub(box, 0.6, 0.18, 0.4, 0.64) }),
    ],
  }),
  layout({
    id: 'sp-2-hero-right',
    name: 'Small left, big right',
    scope: 'spread',
    families: ALL_FAMILIES,
    tags: ['hero', 'asymmetric'],
    slots: [
      slot({ id: 'a', ...sub(box, 0, 0.18, 0.4 - GX, 0.64) }),
      slot({ id: 'b', ...sub(box, 0.4, 0, 0.6, 1) }),
    ],
  }),
  layout({
    id: 'sp-2-captioned',
    name: 'Two photos with captions',
    scope: 'spread',
    families: ALL_FAMILIES,
    tags: ['pair', 'caption', 'journal'],
    slots: [
      slot({ id: 'a', ...sub(L, 0, 0, 1, 0.82) }),
      slot({ id: 'ca', kind: 'text', role: 'caption', ...sub(L, 0, 0.86, 1, 0.14) }),
      slot({ id: 'b', ...sub(R, 0, 0, 1, 0.82) }),
      slot({ id: 'cb', kind: 'text', role: 'caption', ...sub(R, 0, 0.86, 1, 0.14) }),
    ],
  }),
  layout({
    id: 'sp-2-diagonal',
    name: 'Two offset',
    scope: 'spread',
    families: ALL_FAMILIES,
    tags: ['pair', 'offset', 'playful'],
    slots: [
      slot({ id: 'a', ...sub(box, 0.02, 0, 0.46, 0.8) }),
      slot({ id: 'b', ...sub(box, 0.52, 0.2, 0.46, 0.8) }),
    ],
  }),

  // ---------- 3 photos ----------
  layout({
    id: 'sp-3-hero-left',
    name: 'Hero left, two stacked right',
    scope: 'spread',
    families: ALL_FAMILIES,
    tags: ['hero', 'grid', 'asymmetric'],
    slots: [
      slot({ id: 'a', ...sub(box, 0, 0, 0.58 - GX, 1) }),
      slot({ id: 'b', ...sub(box, 0.58, 0, 0.42, 0.5 - G / 2) }),
      slot({ id: 'c', ...sub(box, 0.58, 0.5 + G / 2, 0.42, 0.5 - G / 2) }),
    ],
  }),
  layout({
    id: 'sp-3-hero-right',
    name: 'Two stacked left, hero right',
    scope: 'spread',
    families: ALL_FAMILIES,
    tags: ['hero', 'grid', 'asymmetric'],
    slots: [
      slot({ id: 'a', ...sub(box, 0, 0, 0.42 - GX, 0.5 - G / 2) }),
      slot({ id: 'b', ...sub(box, 0, 0.5 + G / 2, 0.42 - GX, 0.5 - G / 2) }),
      slot({ id: 'c', ...sub(box, 0.42, 0, 0.58, 1) }),
    ],
  }),
  layout({
    id: 'sp-3-row',
    name: 'Three in a row',
    scope: 'spread',
    families: ALL_FAMILIES,
    tags: ['row', 'grid', 'even'],
    slots: grid(box, 'spread', { cols: 3, rows: 1, prefix: 'p' }),
  }),
  layout({
    id: 'sp-3-strip',
    name: 'Three wide bands',
    scope: 'spread',
    families: WIDE_FAMILIES,
    tags: ['stack', 'wide', 'crosses-fold'],
    slots: grid(box, 'spread', { cols: 1, rows: 3 }),
  }),
  layout({
    id: 'sp-3-title',
    name: 'Title band and three photos',
    scope: 'spread',
    families: ALL_FAMILIES,
    tags: ['title', 'row', 'chapter'],
    slots: (() => {
      const [strip, rest] = band(box, 0.18, 'top');
      return [slot({ id: 't', kind: 'text', role: 'title', ...strip }), ...grid(rest, 'spread', { cols: 3, rows: 1 })];
    })(),
  }),

  // ---------- 4 photos ----------
  layout({
    id: 'sp-4-grid',
    name: 'Grid of four',
    scope: 'spread',
    families: ALL_FAMILIES,
    tags: ['grid', 'even'],
    slots: grid(box, 'spread', { cols: 2, rows: 2 }),
  }),
  layout({
    id: 'sp-4-row',
    name: 'Four in a row',
    scope: 'spread',
    families: WIDE_FAMILIES,
    tags: ['row', 'even', 'filmstrip'],
    slots: grid(box, 'spread', { cols: 4, rows: 1 }),
  }),
  layout({
    id: 'sp-4-hero-left',
    name: 'Hero left, three stacked right',
    scope: 'spread',
    families: ALL_FAMILIES,
    tags: ['hero', 'asymmetric'],
    slots: [
      slot({ id: 'a', ...sub(box, 0, 0, 0.6 - GX, 1) }),
      ...grid(sub(box, 0.6, 0, 0.4, 1), 'spread', { cols: 1, rows: 3, prefix: 'b' }),
    ],
  }),
  layout({
    id: 'sp-4-mosaic',
    name: 'Mosaic of four',
    scope: 'spread',
    families: ALL_FAMILIES,
    tags: ['mosaic', 'asymmetric', 'editorial'],
    slots: [
      slot({ id: 'a', ...sub(box, 0, 0, 0.45 - GX, 0.62) }),
      slot({ id: 'b', ...sub(box, 0.45, 0, 0.55, 0.38 - G / 2) }),
      slot({ id: 'c', ...sub(box, 0.45, 0.38 + G / 2, 0.28 - GX, 0.62 - G / 2) }),
      slot({ id: 'd', ...sub(box, 0.73, 0.38 + G / 2, 0.27, 0.62 - G / 2) }),
    ],
  }),
  layout({
    id: 'sp-4-band-title',
    name: 'Four with a title above',
    scope: 'spread',
    families: ALL_FAMILIES,
    tags: ['title', 'grid', 'chapter'],
    slots: (() => {
      const [strip, rest] = band(box, 0.16, 'top');
      return [slot({ id: 't', kind: 'text', role: 'title', ...strip }), ...grid(rest, 'spread', { cols: 4, rows: 1 })];
    })(),
  }),
  layout({
    id: 'sp-4-page-pairs',
    name: 'Two per page',
    scope: 'spread',
    families: TALL_FAMILIES,
    tags: ['grid', 'pairs', 'respects-fold'],
    slots: [
      ...grid(L, 'spread', { cols: 1, rows: 2, prefix: 'l' }),
      ...grid(R, 'spread', { cols: 1, rows: 2, prefix: 'r' }),
    ],
  }),

  // ---------- 5 photos ----------
  layout({
    id: 'sp-5-hero-left',
    name: 'Hero left, four right',
    scope: 'spread',
    families: ALL_FAMILIES,
    tags: ['hero', 'grid', 'asymmetric'],
    slots: [
      slot({ id: 'a', ...sub(box, 0, 0, 0.52 - GX, 1) }),
      ...grid(sub(box, 0.52, 0, 0.48, 1), 'spread', { cols: 2, rows: 2, prefix: 'b' }),
    ],
  }),
  layout({
    id: 'sp-5-mosaic',
    name: 'Mosaic of five',
    scope: 'spread',
    families: ALL_FAMILIES,
    tags: ['mosaic', 'editorial', 'asymmetric'],
    slots: [
      slot({ id: 'a', ...sub(box, 0, 0, 0.34 - GX, 0.55) }),
      slot({ id: 'b', ...sub(box, 0, 0.55 + G / 2, 0.34 - GX, 0.45 - G / 2) }),
      slot({ id: 'c', ...sub(box, 0.34, 0, 0.32 - GX, 1) }),
      slot({ id: 'd', ...sub(box, 0.66, 0, 0.34, 0.45) }),
      slot({ id: 'e', ...sub(box, 0.66, 0.45 + G / 2, 0.34, 0.55 - G / 2) }),
    ],
  }),
  layout({
    id: 'sp-5-strip-caption',
    name: 'Five in a strip with captions',
    scope: 'spread',
    families: WIDE_FAMILIES,
    tags: ['row', 'caption', 'journal', 'filmstrip'],
    slots: (() => {
      const [strip, rest] = band(box, 0.16, 'bottom');
      return [
        ...grid(rest, 'spread', { cols: 5, rows: 1 }),
        slot({ id: 'c', kind: 'text', role: 'caption', ...strip }),
      ];
    })(),
  }),

  // ---------- 6 photos ----------
  layout({
    id: 'sp-6-grid',
    name: 'Grid of six',
    scope: 'spread',
    families: ALL_FAMILIES,
    tags: ['grid', 'even', 'contact-sheet'],
    slots: grid(box, 'spread', { cols: 3, rows: 2 }),
  }),
  layout({
    id: 'sp-6-row',
    name: 'Six in a row',
    scope: 'spread',
    families: WIDE_FAMILIES,
    tags: ['row', 'filmstrip', 'even'],
    slots: grid(box, 'spread', { cols: 6, rows: 1 }),
  }),
  layout({
    id: 'sp-6-hero',
    name: 'Hero with five small',
    scope: 'spread',
    families: ALL_FAMILIES,
    tags: ['hero', 'grid', 'asymmetric'],
    slots: (() => {
      const [strip, rest] = band(box, 0.26, 'bottom');
      return [slot({ id: 'a', ...rest }), ...grid(strip, 'spread', { cols: 5, rows: 1, prefix: 'b' })];
    })(),
  }),
  layout({
    id: 'sp-6-page-thirds',
    name: 'Three per page',
    scope: 'spread',
    families: TALL_FAMILIES,
    tags: ['grid', 'respects-fold'],
    slots: [
      ...grid(L, 'spread', { cols: 1, rows: 3, prefix: 'l' }),
      ...grid(R, 'spread', { cols: 1, rows: 3, prefix: 'r' }),
    ],
  }),

  // ---------- 7–8 photos ----------
  layout({
    id: 'sp-7-mosaic',
    name: 'Mosaic of seven',
    scope: 'spread',
    families: ALL_FAMILIES,
    tags: ['mosaic', 'dense', 'contact-sheet'],
    slots: [
      slot({ id: 'a', ...sub(box, 0, 0, 0.4 - GX, 0.62) }),
      ...grid(sub(box, 0.4, 0, 0.6, 0.62), 'spread', { cols: 3, rows: 1, prefix: 'b' }),
      ...grid(sub(box, 0, 0.62 + G / 2, 1, 0.38 - G / 2), 'spread', { cols: 3, rows: 1, prefix: 'c' }),
    ],
  }),
  layout({
    id: 'sp-8-grid',
    name: 'Grid of eight',
    scope: 'spread',
    families: ALL_FAMILIES,
    tags: ['grid', 'dense', 'contact-sheet'],
    slots: grid(box, 'spread', { cols: 4, rows: 2 }),
  }),
  layout({
    id: 'sp-8-mosaic',
    name: 'Mosaic of eight',
    scope: 'spread',
    families: ALL_FAMILIES,
    tags: ['mosaic', 'dense', 'editorial'],
    slots: [
      slot({ id: 'a', ...sub(box, 0, 0, 0.3 - GX, 0.55) }),
      ...grid(sub(box, 0.3, 0, 0.7, 0.55), 'spread', { cols: 3, rows: 1, prefix: 'b' }),
      ...grid(sub(box, 0, 0.55 + G / 2, 1, 0.45 - G / 2), 'spread', { cols: 4, rows: 1, prefix: 'c' }),
    ],
  }),
  layout({
    id: 'sp-8-page-quads',
    name: 'Four per page',
    scope: 'spread',
    families: ALL_FAMILIES,
    tags: ['grid', 'dense', 'respects-fold'],
    slots: [
      ...grid(L, 'spread', { cols: 2, rows: 2, prefix: 'l' }),
      ...grid(R, 'spread', { cols: 2, rows: 2, prefix: 'r' }),
    ],
  }),

  // ---------- text only ----------
  layout({
    id: 'sp-0-dedication',
    name: 'Dedication',
    scope: 'spread',
    families: ALL_FAMILIES,
    tags: ['text-only', 'title', 'dedication', 'opening'],
    slots: [
      slot({ id: 't', kind: 'text', role: 'title', ...sub(R, 0.1, 0.3, 0.8, 0.16) }),
      slot({ id: 'b', kind: 'text', role: 'body', ...sub(R, 0.1, 0.5, 0.8, 0.28) }),
    ],
  }),
];
